import { AddTask } from './AddTask';
import { Alerts } from './Alerts';
import { SelectedTask } from './SelectedTask';
import type {
  AlertData,
  FreezeDateData,
  LoggedUser,
  ProjectIdList,
  RightPanelDivClassNameProperty,
  SelectedDate,
  SelectedTaskValue,
  TimeEntryCell,
  UnassignedProject,
} from '../../types';

/**
 * Port of `menuComponents/timeEntryRightPanel.jsx` — the three-section accordion.
 *
 * Dropped: `import Popup from "reactjs-popup"` and `import "reactjs-popup/dist/index.css"`.
 * `Popup` was never rendered anywhere in the app, and no element in any file uses
 * `.popup-content`, `.popup-overlay` or `.popup-arrow`, so the stylesheet applied to
 * nothing. Verified by grep across the whole of `checkin-time-entry-ui/src` before
 * removing it.
 *
 * The panel's own `state = {}` is also gone. It was read in exactly two places — the
 * `isDisabled` and `clickInput` props handed to SelectedTask — where it always evaluated
 * to `undefined`, and SelectedTask never reads either. The `undefined` values are still
 * passed so the props reaching SelectedTask are unchanged.
 */

const COLLAPSED = {
  button: 'accordion-button collapsed',
  div: 'accordion-collapse collapse',
} as const;

const EXPANDED = {
  button: 'accordion-button',
  div: 'accordion-collapse collapse show',
} as const;

type PanelName = 'alerts' | 'addTask' | 'selectedTask';

interface TimeEntryRightPanelProps {
  rightPanelDivClassNameProperty: RightPanelDivClassNameProperty | undefined;
  updateStateOfRightPanelDivClassNameProperty: (next: RightPanelDivClassNameProperty) => void;

  alertData: AlertData | undefined;
  selectedDateYear: (date: string) => void;

  /** `boolean | undefined` on purpose — see TimeEntryState.isCopyUnassigedTaskEnabled. */
  isCopyUnassigedTaskEnabled: boolean | undefined;
  addTaskResponse: (response: unknown) => void;
  callUnassignTask: boolean;
  copyUnassignTaskButton: () => void;
  deleteTaskData: unknown;
  deleteGteProjectId: number;
  refreshTimeSheetData: (selectedDate: SelectedDate, empNo: number, projId: number) => void;
  clickInput: boolean;
  projectList: unknown;
  loggedUserDetails: LoggedUser | null;
  loggedUserGteEmployeeNo: number;
  projectIdList: ProjectIdList;
  operationalUserGteEmployeeNo: number;
  selectedDate: SelectedDate;
  addTask?: unknown;
  nonProjectTask: unknown;
  unassignedTasks: UnassignedProject[];
  handleSaveDraft: () => void;

  updateSelectedTaskValue: (selectedTaskValue: SelectedTaskValue) => void;
  freezeDateData: FreezeDateData | undefined;
  userTimeSheetData: unknown;
  selectedTaskValue: SelectedTaskValue;
  validateTimeEntered_TimeType: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  addTimeEntered_TimeType: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  highlightSelectedInputOfTimeType: (
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  enterInvoiceCommentOfTimeTypeProject: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
}

export function TimeEntryRightPanel(props: TimeEntryRightPanelProps) {
  /**
   * Only one section is ever open, so every branch rewrites all three entries.
   *
   * The object is mutated in place and then handed to the parent's setter — exactly as the
   * original did. Cloning it here would look tidier but would break `mainMenu.jsx`, which
   * holds this same object in state and reads fields off it between renders.
   *
   * A section only toggles when its button class is one of the two known strings; anything
   * else leaves all three untouched and still calls the setter.
   */
  const handlePanelCollapse = (
    rightPanelDivClassNameProperty: RightPanelDivClassNameProperty,
    type: PanelName,
  ): void => {
    const current = rightPanelDivClassNameProperty[type];

    if (current.button === COLLAPSED.button) {
      rightPanelDivClassNameProperty.alerts = { ...COLLAPSED };
      rightPanelDivClassNameProperty.addTask = { ...COLLAPSED };
      rightPanelDivClassNameProperty.selectedTask = { ...COLLAPSED };
      rightPanelDivClassNameProperty[type] = { ...EXPANDED };
    } else if (current.button === EXPANDED.button) {
      rightPanelDivClassNameProperty.alerts = { ...COLLAPSED };
      rightPanelDivClassNameProperty.addTask = { ...COLLAPSED };
      rightPanelDivClassNameProperty.selectedTask = { ...COLLAPSED };
    }

    props.updateStateOfRightPanelDivClassNameProperty(rightPanelDivClassNameProperty);
  };

  return (
    <section className="ch-panel-section">
      <div className="accordion accordion-flush" id="accordionFlushExample">
        <div className="accordion-item">
          <span className="accordion-header alert-header">
            <button
              className={props.rightPanelDivClassNameProperty?.alerts?.button}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseOne"
              aria-expanded="false"
              aria-controls="flush-collapseOne"
              onClick={() =>
                handlePanelCollapse(
                  props.rightPanelDivClassNameProperty as RightPanelDivClassNameProperty,
                  'alerts',
                )
              }
              data-toggle="tooltip"
              data-placement="left"
              title="Alerts"
              {...{ alt: 'Alerts' }}
            >
              <span className="ch-accordian-header-icon ch-alert"></span>
              Alerts
            </button>
          </span>
          <div
            id="flush-collapseOne"
            className={props.rightPanelDivClassNameProperty?.alerts?.div}
            data-bs-parent="#accordionFlushExample"
          >
            <div className="accordion-body">
              <Alerts
                alertData={props.alertData}
                selectedDateYear={(date) => {
                  props.selectedDateYear(date);
                }}
              />
            </div>
          </div>
        </div>
        <div className="accordion-item">
          <span className="accordion-header add-task-header">
            <button
              className={props.rightPanelDivClassNameProperty?.addTask?.button}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseTwo"
              aria-expanded="false"
              aria-controls="flush-collapseTwo"
              onClick={() =>
                handlePanelCollapse(
                  props.rightPanelDivClassNameProperty as RightPanelDivClassNameProperty,
                  'addTask',
                )
              }
              data-toggle="tooltip"
              data-placement="left"
              title="Add Task"
              {...{ alt: 'Add Task' }}
            >
              <span className="ch-accordian-header-icon add-task"></span>
              Add Task
            </button>
          </span>
          <div
            id="flush-collapseTwo"
            className={props.rightPanelDivClassNameProperty?.addTask?.div}
            data-bs-parent="#accordionFlushExample"
          >
            <div className="accordion-body">
              <AddTask
                isCopyUnassigedTaskEnabled={props.isCopyUnassigedTaskEnabled}
                addTaskResponse={(response) => {
                  props.addTaskResponse(response);
                }}
                callUnassignTask={props.callUnassignTask}
                copyUnassignTaskButton={() => {
                  props.copyUnassignTaskButton();
                }}
                deleteTaskData={props.deleteTaskData}
                deleteGteProjectId={props.deleteGteProjectId}
                refreshTimeSheetData={(selectedDate, empNo, projId) => {
                  props.refreshTimeSheetData(selectedDate, empNo, projId);
                }}
                clickInput={props.clickInput}
                projectList={props.projectList}
                loggedUserDetails={props.loggedUserDetails}
                loggedUserGteEmployeeNo={props.loggedUserGteEmployeeNo}
                projectIdList={props.projectIdList}
                operationalUserGteEmployeeNo={props.operationalUserGteEmployeeNo}
                selectedDate={props.selectedDate}
                addTask={props.addTask}
                nonProjectTask={props.nonProjectTask}
                unassignedTasks={props.unassignedTasks}
                handleSaveDraft={() => {
                  props.handleSaveDraft();
                }}
              />
            </div>
          </div>
        </div>
        <div className="accordion-item">
          <span className="accordion-header sel-task-header">
            <button
              className={props.rightPanelDivClassNameProperty?.selectedTask?.button}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseThree"
              aria-expanded="true"
              aria-controls="flush-collapseThree"
              onClick={() =>
                handlePanelCollapse(
                  props.rightPanelDivClassNameProperty as RightPanelDivClassNameProperty,
                  'selectedTask',
                )
              }
              data-toggle="tooltip"
              data-placement="left"
              title="Selected Task"
              {...{ alt: 'Selected Task' }}
            >
              <span className="ch-accordian-header-icon selected-task"></span>
              Selected Task
            </button>
          </span>
          <div
            id="flush-collapseThree"
            className={props.rightPanelDivClassNameProperty?.selectedTask?.div}
            data-bs-parent="#accordionFlushExample"
          >
            <div className="accordion-body">
              <SelectedTask
                updateSelectedTaskValue={(selectedTaskValue) => {
                  props.updateSelectedTaskValue(selectedTaskValue);
                }}
                freezeDateData={props.freezeDateData}
                // Both were read off the panel's own empty state in the original, i.e.
                // permanently `true` and `undefined`. SelectedTask reads neither.
                isDisabled={true}
                clickInput={undefined}
                userTimeSheetData={props.userTimeSheetData}
                selectedTaskValue={props.selectedTaskValue}
                validateTimeEntered_TimeType={(value, selectedTaskValue, entry) =>
                  props.validateTimeEntered_TimeType(value, selectedTaskValue, entry)
                }
                addTimeEntered_TimeType={(value, selectedTaskValue, entry) =>
                  props.addTimeEntered_TimeType(value, selectedTaskValue, entry)
                }
                highlightSelectedInputOfTimeType={(selectedTaskValue, entry) =>
                  props.highlightSelectedInputOfTimeType(selectedTaskValue, entry)
                }
                enterInvoiceCommentOfTimeTypeProject={(value, selectedTaskValue, entry) =>
                  props.enterInvoiceCommentOfTimeTypeProject(value, selectedTaskValue, entry)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
